import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { BuildEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createProductBuildLink, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type BuildEventSucceeded = Extract<BuildEvents, { eventTypeName: "BuildEvent.Succeeded" }>;
const renderBuildEventSucceeded = (props: RendererProps<BuildEventSucceeded>): EventContentResult | null => {
    const { event, context, theme } = props;

    const actorTitle = getActorTitle(event, i18next.t("common:status.unknown"));
    const color = getEventColors(theme);

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.buildSucceededTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.buildSucceededDescriptionWithoutData"
                    values={{ actorTitle, entityId: event.entityId }}
                />
            ),
        };
    }

    const version = event.data.version;
    const codename = event.data.codename;
    const productLink = createProductBuildLink(event, context);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.buildSucceededTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.buildSucceededDescription"
                values={{ actorTitle, version, codename }}
                components={{ productLink }}
            />
        ),
    };
};

type BuildEventFailed = Extract<BuildEvents, { eventTypeName: "BuildEvent.Failed" }>;
const renderBuildEventFailed = (props: RendererProps<BuildEventFailed>): EventContentResult | null => {
    const { event, context, theme } = props;

    const actorTitle = getActorTitle(event, i18next.t("common:status.unknown"));
    const color = getEventColors(theme);

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.buildFailedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.buildFailedDescriptionWithoutData"
                    values={{ actorTitle, entityId: event.entityId }}
                />
            ),
        };
    }

    const version = event.data.version;
    const codename = event.data.codename;
    const productLink = createProductBuildLink(event, context);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.buildFailedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.buildFailedDescription"
                values={{ actorTitle, version, codename }}
                components={{ productLink }}
            />
        ),
    };
};

export const renderBuildEventContent = (event: BuildEvents, context: EventContext, theme: Theme): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "BuildEvent.Succeeded":
            return renderBuildEventSucceeded({ event, context, theme });
        case "BuildEvent.Failed":
            return renderBuildEventFailed({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
